import React, { useState, useEffect, useMemo, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes, NavLink } from "react-router-dom";
import Papa from "papaparse";
import Documentation from "./Documentation.js";
import "./App.css";
import {
  FaSearchPlus,
  FaSearchMinus,
  FaSyncAlt,
  FaDownload,
  FaCopy,
  FaChevronDown,
  FaTimes,
  FaCircle,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";

function App() {
  return (
    <Router>
      <div className="App">
        {/* Navigation */}
        <nav className="nav-bar">
          <NavLink to="/" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            Database
          </NavLink>
          <NavLink to="/documentation" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            Documentation
          </NavLink>
        </nav>

        {/* Routes */}
        <Routes>
          <Route path="/" element={<Database />} />
          <Route path="/documentation" element={<Documentation />} />
        </Routes>
      </div>
    </Router>
  );
}

function Database() {
  const [tableData, setTableData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [zoomScale, setZoomScale] = useState(1);
  const [copySuccess, setCopySuccess] = useState("");
  const [filters, setFilters] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState({});

  // Parse the CSV file on initial load
  useEffect(() => {
    Papa.parse("/data.csv", {
      download: true,
      header: true,
      complete: (result) => {
        setTableData(result.data);
        if (result.data.length > 0) {
          const headersList = Object.keys(result.data[0]);
          setHeaders(headersList);
          const initialFilters = {};
          const initialHiddenColumns = {};
          headersList.slice(1).forEach((header) => {
            initialFilters[header] = {
              enabled: false,
              min: "",
              max: "",
            };
            initialHiddenColumns[header] = false;
          });
          setFilters(initialFilters);
          setHiddenColumns(initialHiddenColumns);
        }
      },
    });
  }, []);

  const filteredData = useMemo(() => {
    return tableData.filter((row) => {
      return Object.keys(filters).every((column) => {
        const filter = filters[column];
        if (!filter.enabled) return true;
        const value = parseFloat(row[column]);
        const min = filter.min === "" ? -Infinity : parseFloat(filter.min);
        const max = filter.max === "" ? Infinity : parseFloat(filter.max);
        return !isNaN(value) && value >= min && value <= max;
      });
    });
  }, [filters, tableData]);

  const handleFilterChange = useCallback((column, field, value) => {
    setFilters((prev) => ({
      ...prev,
      [column]: {
        ...prev[column],
        [field]: value,
      },
    }));
  }, []);

  const clearFilter = useCallback((column) => {
    setFilters((prev) => ({
      ...prev,
      [column]: {
        enabled: false,
        min: "",
        max: "",
      },
    }));
  }, []);

  const toggleFilterDropdown = (header) => {
    setActiveFilter(activeFilter === header ? null : header);
  };

  const toggleColumnVisibility = (header) => {
    setHiddenColumns((prev) => ({
      ...prev,
      [header]: !prev[header],
    }));
  };

  const resetCSV = () => {
    setFilters((prev) => {
      const resetFilters = {};
      Object.keys(prev).forEach((key) => {
        resetFilters[key] = { enabled: false, min: "", max: "" };
      });
      return resetFilters;
    });
    setHiddenColumns((prev) => {
      const resetHidden = {};
      Object.keys(prev).forEach((key) => {
        resetHidden[key] = false;
      });
      return resetHidden;
    });
  };

  const unhideAllColumns = () => {
    setHiddenColumns((prev) => {
      const resetHidden = {};
      Object.keys(prev).forEach((key) => {
        resetHidden[key] = false;
      });
      return resetHidden;
    });
  };

  const zoomIn = () => {
    setZoomScale((prev) => Math.min(prev + 0.1, 2));
  };

  const zoomOut = () => {
    setZoomScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  const resetZoom = () => {
    setZoomScale(1);
  };

  const copyCSVText = () => {
    const csvContent = Papa.unparse(filteredData);
    navigator.clipboard.writeText(csvContent)
      .then(() => {
        setCopySuccess("Copied!");
        setTimeout(() => setCopySuccess(""), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy text:", err);
        setCopySuccess("Failed to copy");
      });
  };

  const downloadCSV = () => {
    const csvContent = Papa.unparse(filteredData);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "filtered_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="App" style={{ "--zoom-scale": zoomScale }}>
      <h1></h1>
      <div className="buttons-container">
        <button onClick={copyCSVText} className="custom-button">
          <FaCopy /> Copy File Text {copySuccess && <span className="copy-feedback">{copySuccess}</span>}
        </button>
        <button onClick={downloadCSV} className="custom-button">
          <FaDownload /> Download File
        </button>
        <button onClick={resetCSV} className="custom-button">
          Reset Table Contents
        </button>
        <button onClick={unhideAllColumns} className="custom-button">
          Unhide All
        </button>
        <div className="zoom-controls">
          <button onClick={zoomIn} aria-label="Zoom In">
            <FaSearchPlus />
          </button>
          <button onClick={zoomOut} aria-label="Zoom Out">
            <FaSearchMinus />
          </button>
          <button onClick={resetZoom} aria-label="Reset Zoom">
            <FaSyncAlt />
          </button>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              {headers.map((header, index) => (
                !hiddenColumns[header] && (
                  <th key={index} className="header-cell">
                    <span className="header-text">{header}</span>
                    {header !== "date reviewed [m/d/y]" && header !== "address" && (
                      <div className="header-filter">
                        <button
                          className={`filter-toggle ${filters[header]?.enabled ? "active" : ""}`}
                          onClick={() => toggleFilterDropdown(header)}
                        >
                          <FaChevronDown />
                        </button>
                        {activeFilter === header && (
                          <div className="filter-dropdown">
                            <div className="filter-header">
                              <button
                                className={`toggle-dot ${filters[header]?.enabled ? "active" : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFilterChange(header, "enabled", !filters[header]?.enabled);
                                }}
                              >
                                <FaCircle />
                              </button>
                              <span>Enable Filter</span>
                            </div>
                            <div className="filter-inputs">
                              <input
                                type="number"
                                placeholder="Min"
                                value={filters[header]?.min}
                                onChange={(e) => handleFilterChange(header, "min", e.target.value)}
                                className="filter-input"
                              />
                              <input
                                type="number"
                                placeholder="Max"
                                value={filters[header]?.max}
                                onChange={(e) => handleFilterChange(header, "max", e.target.value)}
                                className="filter-input"
                              />
                            </div>
                            <button
                              className="clear-filter"
                              onClick={(e) => {
                                e.stopPropagation();
                                clearFilter(header);
                              }}
                            >
                              <FaTimes /> Clear Filter
                            </button>
                            <button
                              className="clear-filter"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleColumnVisibility(header);
                              }}
                            >
                              {hiddenColumns[header] ? <FaEye /> : <FaEyeSlash />} {hiddenColumns[header] ? "Show" : "Hide Column"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </th>
                )
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {headers.map((header, colIndex) => (
                  !hiddenColumns[header] && <td key={colIndex}>{row[header]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default App;